import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Shield, ChevronLeft, CheckCircle } from 'lucide-react';
import { detectGPC, getOrCreateAnonymousId, loadConsent, saveConsent } from '../utils/consent';
import { applyConsent } from '../utils/trackingLoader';

const OPTOUT_ENDPOINT = '/api/v2/privacy/optout';

const CATEGORIES_OF_PI = [
  {
    category: 'Identifiers',
    examples: 'Name, email address, IP address, account identifiers, device identifiers, cookies.',
    sources: 'Directly from you, from your device when you visit, from authentication providers (Auth0).',
    purposes: 'Operate the app, authenticate you, send transactional email, prevent fraud, respond to support requests.',
    recipients: 'Auth0, Stripe (for billing), Postmark (for email).',
  },
  {
    category: 'Commercial information',
    examples: 'Subscription tier, billing history, products and services purchased.',
    sources: 'Directly from you when you subscribe; from Stripe.',
    purposes: 'Provide your subscription, take payment, fulfill tax and accounting obligations.',
    recipients: 'Stripe, our accountants, tax authorities as required by law.',
  },
  {
    category: 'Internet or other electronic network activity',
    examples: 'Pages visited, referring URLs, click attribution parameters (utm_*, gclid, fbclid), session recordings (Clarity).',
    sources: 'From your browser. Session recordings only with your analytics consent.',
    purposes: 'Understand site usage, measure marketing performance, debug UX issues.',
    recipients: 'Google Analytics 4, Microsoft Clarity, Vercel Analytics, Meta (Pixel), Rewardful (affiliate attribution). Only after you consent.',
  },
  {
    category: 'Inferences drawn from other personal information',
    examples: 'Persona segments (such as legal professional, researcher) derived from referrer and click patterns.',
    sources: 'Internally derived from the categories above.',
    purposes: 'Personalize on-site content, prioritize feature roadmap.',
    recipients: 'Used internally only; not sold or shared with third parties for cross-context behavioral advertising.',
  },
  {
    category: 'Content you process through the app',
    examples: 'Filenames, folder paths, sort prompts. File contents only if you enable content-sorting features.',
    sources: 'From your device when you run a sort.',
    purposes: 'Provide AI sorting, entity extraction, and chat assistance.',
    recipients: 'OpenAI (default LLM provider), Anthropic (if you select it via BYOK), or your local Ollama instance (no transit).',
  },
];

const STATE_RIGHTS = [
  'Right to Know what personal information we collect, use, disclose, and share.',
  'Right to Delete personal information we hold about you.',
  'Right to Correct inaccurate personal information.',
  'Right to Opt-Out of the sale or sharing of personal information (including cross-context behavioral advertising).',
  'Right to Limit the use and disclosure of Sensitive Personal Information.',
  'Right to Non-Discrimination for exercising your privacy rights.',
];

const PrivacyChoicesPage = () => {
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [optedOut, setOptedOut] = useState(false);

  useEffect(() => {
    const current = loadConsent();
    if (current.decided && !current.analytics && !current.marketing) {
      setOptedOut(true);
    }
    if (detectGPC()) {
      setOptedOut(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setStatus('submitting');

    // Persist the local choice immediately. We treat the form as an
    // affirmative opt-out request: turn off analytics + marketing
    // categories regardless of prior state.
    const choice = saveConsent({
      analytics: false,
      marketing: false,
      preferences: loadConsent().preferences,
      gpc: detectGPC(),
    });
    applyConsent(choice);

    try {
      const res = await fetch(OPTOUT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          anonymous_id: getOrCreateAnonymousId(),
          email: email || undefined,
          reason: 'privacy_choices_form',
          ts: new Date().toISOString(),
          gpc: detectGPC(),
        }),
        credentials: 'include',
      });
      if (!res.ok) {
        // Local opt-out is still in effect; surface a soft error but
        // still report success to the user since their browser-level
        // tracking is already disabled.
        setStatus('success');
        setOptedOut(true);
        return;
      }
      setStatus('success');
      setOptedOut(true);
    } catch (err) {
      setStatus('success'); // local choice still applies
      setOptedOut(true);
      setError('We could not reach our servers, but your local opt-out choice has been saved.');
    }
  };

  return (
    <div className="min-h-screen bg-black text-apple-100 py-24">
      <Helmet>
        <title>Your Privacy Choices - Sortio</title>
        <meta
          name="description"
          content="US state privacy notice and opt-out form for Sortio. Exercise your CCPA, CPRA, and equivalent state rights."
        />
      </Helmet>

      <div className="absolute top-0 inset-x-0 h-[500px] bg-gradient-to-b from-apple-700/10 to-transparent"></div>

      <div className="container mx-auto px-6 max-w-4xl relative z-10">
        <Link to="/" className="inline-flex items-center text-apple-300 hover:text-sortio-primary mb-12 transition-colors group">
          <ChevronLeft className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
          <span>Back to Home</span>
        </Link>

        <div className="mb-16 flex flex-col items-center">
          <div className="w-14 h-14 rounded-full bg-apple-700/60 backdrop-blur-sm flex items-center justify-center mb-6 border border-apple-600/30">
            <Shield className="w-6 h-6 text-sortio-primary" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-center">
            Your <span className="apple-gradient-text">Privacy Choices</span>
          </h1>
          <p className="text-apple-300 mt-4 text-center max-w-2xl">
            US state privacy notice for residents of California, Colorado, Connecticut, Virginia, Utah, Texas, Oregon, and any other state with comparable rights.
          </p>
        </div>

        <div className="apple-panel p-8 md:p-12 rounded-apple backdrop-blur-sm mb-12">
          <div className="space-y-10">

            <section>
              <h2 className="text-xl font-medium text-apple-50 mb-3">Categories of personal information we collect</h2>
              <p className="text-apple-300 leading-relaxed mb-4">
                The table below describes the categories of personal information we collect, the sources from which it is collected, the business or commercial purpose for which it is used, and the categories of third parties with whom it is shared (CPRA section 1798.130).
              </p>
              <div className="space-y-4">
                {CATEGORIES_OF_PI.map((row, i) => (
                  <div key={i} className="rounded-lg bg-apple-700/20 border border-apple-600/20 p-4 text-sm">
                    <h3 className="text-apple-50 font-medium mb-2">{row.category}</h3>
                    <dl className="grid grid-cols-1 md:grid-cols-2 gap-2 text-apple-300">
                      <div><dt className="text-apple-400 text-xs uppercase tracking-wider">Examples</dt><dd>{row.examples}</dd></div>
                      <div><dt className="text-apple-400 text-xs uppercase tracking-wider">Sources</dt><dd>{row.sources}</dd></div>
                      <div><dt className="text-apple-400 text-xs uppercase tracking-wider">Purposes</dt><dd>{row.purposes}</dd></div>
                      <div><dt className="text-apple-400 text-xs uppercase tracking-wider">Recipients</dt><dd>{row.recipients}</dd></div>
                    </dl>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h2 className="text-xl font-medium text-apple-50 mb-3">Your rights</h2>
              <ul className="space-y-2">
                {STATE_RIGHTS.map((r, i) => (
                  <li key={i} className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-sortio-secondary mr-3 mt-0.5 flex-shrink-0" aria-hidden="true" />
                    <span className="text-apple-300 text-sm">{r}</span>
                  </li>
                ))}
              </ul>
              <p className="text-apple-300 leading-relaxed mt-4 text-sm">
                We honor browser-level{' '}
                <strong>Global Privacy Control (GPC)</strong> signals as a valid opt-out of sale and sharing for the device used. To exercise the rights to know, delete, or correct, visit our{' '}
                <Link to="/privacy-requests" className="text-sortio-primary hover:text-sortio-primary/80">Privacy Requests</Link>{' '}
                page.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-medium text-apple-50 mb-3">Opt out of sale and sharing</h2>
              <p className="text-apple-300 leading-relaxed mb-4 text-sm">
                Submit the form below to opt out of cross-context behavioral advertising and any sale or sharing of your personal information with third-party advertising partners. We also turn off non-essential analytics and marketing cookies on this browser. Providing an email is optional. If you provide one, we will also suppress server-side tracking on accounts associated with that email.
              </p>

              {optedOut && status !== 'submitting' ? (
                <div className="p-4 rounded-lg bg-sortio-secondary/10 border border-sortio-secondary/30 text-sm text-apple-100">
                  You have opted out. We will not share your information with advertising partners on this browser.
                  {error && <p className="text-apple-300 text-xs mt-2">{error}</p>}
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="optout-email" className="block text-sm text-apple-200 mb-1">
                      Email (optional)
                    </label>
                    <input
                      id="optout-email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full rounded-lg bg-apple-700/40 border border-apple-600/40 px-3 py-2 text-apple-50 placeholder:text-apple-400 focus:outline-none focus:ring-2 focus:ring-sortio-primary"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="inline-flex items-center px-5 py-2.5 rounded-full bg-sortio-primary hover:bg-sortio-primary/90 text-white text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {status === 'submitting' ? 'Submitting...' : 'Do Not Sell or Share My Personal Information'}
                  </button>
                </form>
              )}
            </section>

            <section>
              <h2 className="text-xl font-medium text-apple-50 mb-3">Contact</h2>
              <p className="text-apple-300 leading-relaxed text-sm">
                Privacy contact: <a href="mailto:marcus@getsortio.com" className="text-sortio-primary hover:text-sortio-primary/80">marcus@getsortio.com</a>. Authorized agent requests are accepted; we may require documentation of authority and identity verification.
              </p>
            </section>

          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyChoicesPage;
